import { AskBackSlot, ASK_BACK_SLOT_STATE, Candidate, GateSelection, PickAskBackGate } from './types';
import { GateReply } from './apiHelper';

/**
 * One answered ambiguous slot, as the picker form hands it back. Carries the whole
 * Candidate rather than just its org_id so the caller can build the turn's display
 * text without re-walking the slots.
 *
 * Lives here, not in the component, because useGlobalAskAica consumes it: a store
 * module must not import a type from a component it has no other business knowing
 * about (and CompanyPicker already imports from this file, so the reverse would
 * close a cycle).
 */
export interface PickedSlot {
    slot_id: string;
    candidate: Candidate;
}

/**
 * The slot_id stood in for a LEGACY ask_back payload — one that carried
 * `candidates` and no `slots` at all, i.e. a pre-RECUR-27888 Athena. It is a
 * local placeholder and MUST NOT be sent on the wire: the server that produced
 * such a payload has never heard of slot ids, so the reply for it is the legacy
 * `selected_org_id` shape alone (see buildGateReply).
 */
export const LEGACY_SLOT_ID = '__legacy_single_slot__';

export interface NormalisedPick {
    /** One entry per name the user wrote, in wire order. */
    slots: AskBackSlot[];
    /** True when the payload carried NO `slots` array — the server is older than
     * this feature, so its reply must be legacy-shaped. */
    isLegacyOnly: boolean;
}

/**
 * Reconciles the two shapes an ask_back(kind:"pick") can arrive in — frozen
 * contract v4 §4/§5 — so no caller has to know which Athena it is talking to:
 *
 *  - `slots` present  → the RECUR-27888 shape. Used as-is. The legacy
 *    `candidates` array that rides ALONGSIDE it for a single ambiguous slot is
 *    ignored here: `slots` is the same information, with a slot_id.
 *  - `slots` absent, `candidates` present → the shape live in production today.
 *    Folded into ONE synthetic ambiguous slot so the form renders identically;
 *    flagged isLegacyOnly so the reply goes back in the shape that server
 *    understands.
 *
 * `written` is left empty for the synthetic slot on purpose — the legacy payload
 * never carried the typed name, and the single-slot form shows no per-slot label
 * anyway (that is what keeps it byte-identical to the shipped build).
 */
export const normaliseAskBackSlots = (gate: PickAskBackGate | null | undefined): NormalisedPick => {
    if (!gate) return { slots: [], isLegacyOnly: false };

    if (Array.isArray(gate.slots) && gate.slots.length > 0) {
        return { slots: gate.slots.map(guardUnsatisfiableSlot), isLegacyOnly: false };
    }

    if (Array.isArray(gate.candidates) && gate.candidates.length > 0) {
        return {
            slots: [{ slot_id: LEGACY_SLOT_ID, written: '', state: ASK_BACK_SLOT_STATE.AMBIGUOUS, candidates: gate.candidates }],
            isLegacyOnly: true,
        };
    }

    return { slots: [], isLegacyOnly: false };
};

/**
 * A slot that claims `ambiguous` but offers nothing to pick can never be
 * satisfied, and Confirm — which requires EVERY ambiguous slot answered — would
 * stay disabled forever with no way out but "✕". Demote it to `search_failed`,
 * whose copy ("we couldn't look this up") is the honest description of that
 * payload; `not_in_book` is NOT used here because it asserts the name is absent
 * from the caller's portfolio, which this payload does not establish.
 */
const guardUnsatisfiableSlot = (slot: AskBackSlot): AskBackSlot =>
    slot.state === ASK_BACK_SLOT_STATE.AMBIGUOUS && !slot.candidates?.length ? { ...slot, state: ASK_BACK_SLOT_STATE.SEARCH_FAILED } : slot;

/** Slots the user must answer before Confirm enables. Anything that is not an
 * ambiguous slot WITH candidates is informational — including an unrecognised
 * state from a newer Athena, which must never gate the form. */
export const requiresChoice = (slot: AskBackSlot): boolean => slot.state === ASK_BACK_SLOT_STATE.AMBIGUOUS && !!slot.candidates?.length;

export const ambiguousSlots = (slots: AskBackSlot[]): AskBackSlot[] => slots.filter(requiresChoice);

/**
 * Builds the gate_reply body for a confirmed picker form (frozen contract v4 §5).
 *
 * 🔴 The dual-shape branch is a DEPLOY-ORDER guard, not redundancy. aica-app and
 * Athena deploy independently, so this build can reach production before a
 * RECUR-27888 Athena does. A `selections[]`-only reply to an Athena that only
 * understands `selected_org_id` breaks every picker tap on the LIVE portfolio
 * surface. So for the single-ambiguous-slot case — the only case that exists
 * today, and therefore the only one that can regress — both keys are sent, and
 * the new server prefers `selections`. Removable once a RECUR-27888 Athena is in
 * production (see the hand-off note on RECUR-27773).
 */
export const buildGateReply = (picked: GateSelection[], opts: { isLegacyOnly: boolean; ambiguousCount: number }): GateReply => {
    // A legacy payload has no server-side slot ids to echo — sending `selections`
    // with our local placeholder would be a request that server cannot validate.
    if (opts.isLegacyOnly) return { selected_org_id: picked[0]?.selected_org_id };

    if (opts.ambiguousCount === 1 && picked.length === 1) {
        return { selections: picked, selected_org_id: picked[0].selected_org_id };
    }

    return { selections: picked };
};
